import styled from '@emotion/styled'

import { Colors } from '../../theme'
import { useArcadeMigrationFeatureFlag } from '../ArcadeComponentMigration/ArcadeComponentMigrationProvider'
import { avatarSizeDimensions } from '../Avatar/ArcadeAvatarBase'
import { ArcadeIconMat } from './ArcadeIconMat'

type CircleVariantProps = {
  color: Colors
  backgroundColor: Colors
}

type CircleProps = CircleVariantProps & {
  size: string
}

const Circle = styled.div<CircleProps>`
  color: ${props => props.theme.colors[props.color]};
  background: ${props => props.theme.colors[props.backgroundColor]};
  border-radius: 50%;
  width: ${props => props.size};
  height: ${props => props.size};
  display: grid;
  place-items: center;
`

type Variant = 'tint' | 'danger' | 'inverse' | 'subtle'
type Size = 'xsmall' | 'small' | 'medium'

export interface IconMatProps {
  variant?: Variant
  size?: Size
  children?: React.ReactNode
}

/**
 * A background fill intended to underlay an icon, to be frequently used with Arcade icons.
 * Assumes child icon inherits its fill from `currentColor`, and is is appropriately sized where
 *
 * - size of "xsmall" assumes a 16x16 icon
 * - size of "small" assumes a 24x24 icon
 * - size of "medium" (default) assumes a 32x32 icon
 */
/** @deprecated Use ArcadeIconMat instead */
export const IconMat = ({ variant = 'tint', size = 'medium', children }: IconMatProps) => {
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('icon')

  if (isArcadeMigrationEnabled) {
    return (
      <ArcadeIconMat variant={variant} size={size}>
        {children}
      </ArcadeIconMat>
    )
  }

  const variants: Record<Variant, CircleVariantProps> = {
    tint: {
      color: 'primaryButtonTint',
      backgroundColor: 'primary',
    },
    danger: {
      color: 'primaryButtonTint',
      backgroundColor: 'iconDanger',
    },
    inverse: {
      color: 'background',
      backgroundColor: 'icon',
    },
    subtle: {
      color: 'iconSubtle',
      backgroundColor: 'backgroundSubtle',
    },
  }
  return (
    <Circle {...variants[variant]} size={avatarSizeDimensions[size].rem}>
      {children}
    </Circle>
  )
}
